import { EFinancingCompany } from '@if/core/types'
import {
  LOGIN_PREFERENCE_KEY,
  PREFERS_DARK_SETTING,
  PreferencesContext,
  SELECTED_COMPANY_LOCAL_STORAGE,
} from '@src/contexts/preferences-context'
import { useCallback, useContext, useMemo, useState, type JSX } from 'react'

interface Props {
  children: JSX.Element
}

const PreferencesProvider = ({ children }: Props) => {
  const [prefersDarkMode, setPrefersDarkModeState] = useState<boolean>(() => {
    const stored = localStorage.getItem(PREFERS_DARK_SETTING)
    if (stored) return stored === 'true'
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  const [loginPreference, setLoginPreferenceState] = useState<number>(() => {
    const stored = localStorage.getItem(LOGIN_PREFERENCE_KEY)
    return stored ? Number(stored) : 0
  })

  const [selectedCompany, setSelectedCompanyState] = useState<EFinancingCompany | null>(() => {
    const stored = localStorage.getItem(SELECTED_COMPANY_LOCAL_STORAGE)
    return stored as EFinancingCompany | null
  })

  const setDarkMode = useCallback((value: boolean) => {
    setPrefersDarkModeState(value)
    localStorage.setItem(PREFERS_DARK_SETTING, value.toString())
  }, [])

  const setLoginPreference = useCallback((value: number) => {
    setLoginPreferenceState(value)
    localStorage.setItem(LOGIN_PREFERENCE_KEY, value.toString())
  }, [])

  const setSelectedCompany = useCallback((company: EFinancingCompany) => {
    setSelectedCompanyState(company)
    localStorage.setItem(SELECTED_COMPANY_LOCAL_STORAGE, company)
  }, [])

  const ctx = useMemo(
    () => ({
      prefersDarkMode,
      setDarkMode,
      loginPreference,
      setLoginPreference,
      selectedCompany,
      setSelectedCompany,
    }),
    [prefersDarkMode, setDarkMode, loginPreference, setLoginPreference, selectedCompany, setSelectedCompany],
  )

  return <PreferencesContext.Provider value={ctx}>{children}</PreferencesContext.Provider>
}

export const usePreferences = () => {
  const ctx = useContext(PreferencesContext)
  if (!ctx) throw new Error('usePreferences must be used within PreferencesProvider')
  return ctx
}

export default PreferencesProvider
